import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import SectionHeader from "@/components/feature/SectionHeader";

const HIGHLIGHTS = [
  {
    icon: "ri-closed-captioning-line",
    title: "中文字幕资讯",
    desc: "汇总最新影视作品的中文字幕更新情况，标注字幕语言与版本信息。",
  },
  {
    icon: "ri-filter-3-line",
    title: "便捷筛选检索",
    desc: "支持按类型、年份、排序过滤，也能通过关键词快速定位目标作品。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "本地收藏管理",
    desc: "收藏记录保存在浏览器本地，无需登录即可随时标记想看的内容。",
  },
  {
    icon: "ri-device-line",
    title: "多端适配",
    desc: "桌面与手机端均可流畅浏览，移动端提供折叠筛选与滑动列表。",
  },
];

export default function About() {
  useDocumentMeta(
    "关于我们 - 最近中文字幕",
    "了解最近中文字幕的网站定位、内容范围、版权说明与免责声明，以及联系方式与反馈渠道。",
    "关于最近中文字幕,中文字幕站,影视资源平台,版权声明,免责声明"
  );

  return (
    <div className="animate-fade-up">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">关于我们</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于最近中文字幕
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            一个专注影视资源与中文字幕更新信息的资讯展示平台。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
        <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <SectionHeader title="网站定位" icon="ri-compass-3-line" />
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            最近中文字幕致力于为中文影视爱好者提供最新影视作品的中文字幕更新信息。
            我们涵盖电影与电视剧两个主要类别，帮助用户及时获取带中文字幕的影视作品信息并浏览详情。
            站内以快速展示最新资源、方便按类别筛选、提供丰富的影视简介与字幕信息为核心目标，
            支持用户高效查找感兴趣的内容。
          </p>
        </section>

        <section className="mt-8">
          <SectionHeader title="平台特点" icon="ri-sparkling-2-line" />
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                  <i className={item.icon}></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <SectionHeader title="版权与免责声明" icon="ri-shield-line" />
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                本站为影视信息展示平台，所有影视名称、简介、海报均为虚构演示内容，仅用于功能演示。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                本站不提供真实视频播放、在线观看、下载或存储服务，页面的剧集与播放信息均为演示状态。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                如涉及版权问题或内容异议，请通过下方联系方式与我们联系，我们将及时处理。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-accent-600"></i>
                收藏、筛选、搜索等功能均在前端本地完成，本站不收集用户个人身份信息。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-8">
            <SectionHeader title="联系方式与反馈" icon="ri-customer-service-2-line" />
            <p className="mt-4 text-sm leading-relaxed text-secondary-900">
              如果你对本站的内容、字幕信息或功能有任何建议，欢迎通过以下方式联系我们。
            </p>
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3 rounded-md border border-secondary-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">反馈邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">
                    feedback@zysub-example.com
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md border border-secondary-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">字幕更新时段</p>
                  <p className="text-sm font-medium text-foreground-900">每日 09:00 / 21:00</p>
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs text-foreground-500">
              以上邮箱为演示地址，仅用于展示联系方式与反馈入口。
            </p>
          </div>
        </section>

        <section className="mt-8 flex flex-col items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              开始探索带中文字幕的影视作品
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              进入分类专区，按类型与年份筛选你感兴趣的内容。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/category/movie"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-film-line"></i>
              电影专区
            </Link>
            <Link
              to="/category/tv"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-tv-2-line"></i>
              电视剧专区
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}